import React, { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { motion } from "framer-motion";
import { useAuth } from "../../contexts/AuthContext";
import LoadingSpinner from "../../components/LoadingSpinner";

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
`;

const Card = styled(motion.div)`
  background: white;
  border-radius: 20px;
  padding: 3rem;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  max-width: 400px;
  width: 100%;
`;

const Title = styled.h1`
  color: #2d3436;
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 1rem;
`;

const Message = styled.p`
  color: #636e72;
  font-size: 1.1rem;
  margin-bottom: 2rem;
`;

const SuccessIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1rem;
  color: #00b894;
`;

const ErrorIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1rem;
  color: #e84393;
`;

const GoogleCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithToken, loginWithGoogleData } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent multiple executions
    if (hasProcessed.current) {
      return;
    }

    const token = searchParams.get("token");
    const success = searchParams.get("success");
    const error = searchParams.get("error");
    const userDataParam = searchParams.get("user");

    console.log("Google Callback Debug:", {
      token: token ? `${token.substring(0, 20)}...` : "null",
      success,
      error,
      userDataParam: userDataParam ? "exists" : "null",
    });

    // Mark as processed immediately
    hasProcessed.current = true;

    const handleGoogleAuth = async () => {
      if (success === "true" && token) {
        // Successfully authenticated with Google
        let loginResult;
        if (userDataParam) {
          // Use user data from URL (Google OAuth)
          try {
            const userData = JSON.parse(decodeURIComponent(userDataParam));
            loginResult = await loginWithGoogleData(token, userData);
          } catch (parseError) {
            console.error("Error parsing user data:", parseError);
            loginResult = await loginWithToken(token);
          }
        } else {
          // Fallback to token verification
          loginResult = await loginWithToken(token);
        }

        // Redirect based on user role
        let redirectPath = "/";
        if (loginResult?.success && loginResult?.user) {
          if (loginResult.user.role === "vendor") {
            redirectPath = "/vendor";
          } else if (
            loginResult.user.role === "admin" ||
            loginResult.user.role === "employee"
          ) {
            redirectPath = "/admin";
          } else if (loginResult.user.role === "customer") {
            redirectPath = "/";
          }
        }

        // Redirect after a short delay
        setTimeout(() => {
          navigate(redirectPath);
        }, 2000);
      } else if (success === "false" || error) {
        // Authentication failed
        setTimeout(() => {
          navigate("/login");
        }, 3000);
      } else {
        // Invalid callback
        navigate("/login");
      }
    };

    handleGoogleAuth();
  }, [searchParams, navigate]); // Removed loginWithToken and loginWithGoogleData from dependencies

  const token = searchParams.get("token");
  const success = searchParams.get("success");
  const error = searchParams.get("error");

  if (success === "true" && token) {
    return (
      <Container>
        <Card
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <SuccessIcon>✅</SuccessIcon>
          <Title>Welcome to NourishGY!</Title>
          <Message>
            You have successfully signed in with Google. Redirecting you to the
            home page...
          </Message>
          <LoadingSpinner text="Redirecting..." />
        </Card>
      </Container>
    );
  }

  if (success === "false" || error) {
    return (
      <Container>
        <Card
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <ErrorIcon>❌</ErrorIcon>
          <Title>Authentication Failed</Title>
          <Message>
            There was an error signing in with Google. Please try again or use
            email and password.
          </Message>
          <LoadingSpinner text="Redirecting to login..." />
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <Card
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <LoadingSpinner text="Processing authentication..." />
      </Card>
    </Container>
  );
};

export default GoogleCallback;
